import { useState, useEffect } from 'react';
import Connection from './Connection';
import {
    http,
    CLINIC,
    LAB,
    ADMIN,
    fetchAllClinics,
    fetchAllLabs,
    getRole,
} from '../../helpers';
import {
    Header,
    AddConnectionForm,
    Modal,
    Spinner,
    Pagination,
    ClinicPicker,
    LabPicker,
} from '../../components';
import './styles.scss';

const getTitleBasedOnRole = (role) => {
    if (role === LAB) return 'Clinic Connections';
    if (role === CLINIC) return 'Lab Connections';
    return 'Connections';
};

const getInitialPagination = () => ({
    number: 0,
    totalPages: 1,
    first: true,
    last: true,
});

const sortConnections = (data) => {
    const compare = (a, b) => a.name.toLowerCase() < b.name.toLowerCase();
    return data.content.sort((a, b) => {
        if (a.clinic.name === b.clinic.name) {
            if (compare(b.lab, a.lab)) return 1;
            if (compare(a.lab, b.lab)) return -1;
            return 0;
        }
        if (compare(b.clinic, a.clinic)) return 1;
        if (compare(a.clinic, b.clinic)) return -1;
        return 0;
    });
};

function Connections() {
    const [role] = useState(getRole());
    const [connections, setConnections] = useState(null);
    const [pagination, setPagination] = useState(getInitialPagination());
    const [selectedClinic, setSelectedClinic] = useState('');
    const [selectedLab, setSelectedLab] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [entities, setEntities] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [labs, setLabs] = useState([]);
    const title = getTitleBasedOnRole(role);

    const getConnections = (page = 0, clinicId = selectedClinic, labId = selectedLab) => {
        const relativeUrl = `/case-api/connections?size=8&page=${page || 0}&clinicArchived=false&labArchived=false&clinicId=${clinicId || ''}&labId=${labId || ''}`;
        http.get(relativeUrl)
            .then(({ data }) => {
                setConnections(sortConnections(data));
                setPagination({
                    number: data.number,
                    totalPages: data.totalPages,
                    first: data.first,
                    last: data.last,
                });
            })
            .catch((error) => {
                console.warn(error);
            });
    };

    const pagingParams = 'page=0&size=1000&sort=name,id';

    const getPotentialConnections = () => {
        if (role === CLINIC) {
            http.get(`/case-api/connections/search/labs?${pagingParams}`)
                .then(({ data }) => setEntities(data.content))
                .catch(console.error);
        } else if (role === LAB) {
            http.get(`/case-api/connections/search/clinics?${pagingParams}`)
                .then(({ data }) => setEntities(data.content))
                .catch(console.error);
        } else if (role === ADMIN) {
            if (selectedClinic) {
                http.get(`/case-api/connections/search/labs?${pagingParams}&clinicId=${selectedClinic}`)
                    .then(({ data }) => setEntities(data.content))
                    .catch(console.error);
            } else if (selectedLab) {
                http.get(`/case-api/connections/search/clinics?${pagingParams}&labId=${selectedLab}`)
                    .then(({ data }) => setEntities(data.content))
                    .catch(console.error);
            }
        }
    };

    const onConfirmDelete = (id) => {
        http.delete(`/case-api/connections/${id}`)
            .then((res) => {
                if (res.status >= 200 && res.status < 300) {
                    getConnections();
                }
            })
            .catch((error) => {
                console.warn(error);
            });
    };

    const getProps = (connection) => {
        if (role === LAB) return connection.clinic;
        if (role === CLINIC) return connection.lab;
        if (role === ADMIN) {
            if (selectedLab) return connection.clinic;
            if (selectedClinic) return connection.lab;
        }
        return connection.lab;
    };

    const onSelectClinic = (clinicId) => {
        setSelectedClinic(clinicId);
        if (clinicId) {
            setSelectedLab('');
            getConnections(0, clinicId, '');
        } else {
            setConnections([]);
        }
    };
    const onSelectLab = (labId) => {
        setSelectedLab(labId);
        if (labId) {
            setSelectedClinic('');
            getConnections(0, '', labId);
        } else {
            setConnections([]);
        }
    };

    useEffect(() => {
        getPotentialConnections();
    }, [connections, selectedClinic, selectedLab]);

    useEffect(() => {
        getPotentialConnections();
        Promise.all([fetchAllClinics(), fetchAllLabs()])
            .then(([allClinics, allLabs]) => {
                setClinics(allClinics);
                setLabs(allLabs);
            })
            .catch((error) => console.warn(error));
    }, []);

    useEffect(() => {
        if (role !== ADMIN) getConnections();
    }, []);

    const view = (
        <div className="connections">
            { ((selectedLab || selectedClinic)) && (
                <div className="add-connection">
                    <div className="add-connection-content" onClick={() => setShowModal(true)} role="presentation">
                        <div className="add-user-icon">
                            <i className="material-icons">person_add</i>
                        </div>
                        <h3>Add New Contact</h3>
                    </div>
                </div>
            )}
            {
                connections && connections.length
                    ? connections.map((c) => <Connection key={c.id} props={getProps(c)} onDeleteConnection={() => onConfirmDelete(c.id)} />)
                    : (
                        role === ADMIN && !selectedClinic && !selectedLab
                            ? <div className="text-red text-align-center">Please select a clinic or a lab</div>
                            : <div className="text-red text-align-center">No Connections</div>
                    )
            }
            <div className="connections-pagination">
                <Pagination onChangePage={(newPage) => getConnections(newPage)} pagination={pagination} />
            </div>
        </div>
    );

    return (
        <div>
            <div className="connections-container">
                <Header label={title} />
                { role === ADMIN && (
                    <div className="connections-clinicpicker-container">
                        <ClinicPicker
                            clinics={clinics}
                            selectedClinic={selectedClinic}
                            onSelectClinic={onSelectClinic}
                        />
                        <LabPicker
                            labs={labs}
                            selectedLab={selectedLab}
                            onSelectLab={onSelectLab}
                        />
                    </div>
                ) }
                {
                    !connections && (selectedClinic || selectedLab) ? <Spinner type="page" /> : view
                }
                <Modal show={showModal}>
                    <i className="material-icons" role="presentation" onClick={() => setShowModal(false)}>close</i>
                    {/* rendering logic below will trigger AddConnectionForm cleanup function upon close */}
                    { showModal && (
                        <AddConnectionForm
                            chosenLab={selectedLab}
                            chosenClinic={selectedClinic}
                            closeModalHandler={() => setShowModal(false)}
                            entities={entities}
                            refreshLabs={() => {
                                setShowModal(false);
                                getConnections();
                            }}
                            role={role}
                        />
                    ) }
                </Modal>
            </div>
        </div>
    );
}

export default Connections;
